import Axios from "axios";
import React, {createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useState,} from "react";
import i18n from "i18next";
import {useApolloClient} from "@apollo/client";
import {LocalStorageKey} from "../enums/LocalStorageKey";
import {ISyncStorage} from "../interfaces/ISyncStorage";
import {hr} from "../locales/hr";
import {languageOverrides} from "../locales/overrides";
import {IdleContext} from "../contexts/IdleContext";
import {UserQuery, UserQueryResult, useUserQuery,} from "../graphql-types";

interface IAuthContext {
    Token: string | null;
    Logout: () => Promise<void>;
    RefreshToken: () => Promise<any>;
    UserInfo?: UserQuery;
    SetToken: (token: string | null) => void;
    IsDeleted: boolean;
    FetchUserInfo: () => Promise<any>;
    SilentSetToken: (token: string | null) => void;
}

interface IAuthContextEntryProps {
    storage: ISyncStorage;
}

/** 401 and 403 from /api/token both mean the stored session cannot be used any more. */
export const isRejectedSession = (status?: number): boolean => status === 401 || status === 403;

/** The user query was refused because the token was not accepted (HotChocolate AUTH_NOT_AUTHENTICATED). */
export const isUnauthenticatedGraphQLError = (error?: {graphQLErrors?: readonly {extensions?: Record<string, unknown>}[]}): boolean =>
    !!error?.graphQLErrors?.some((graphQLError) => graphQLError?.extensions?.code === "AUTH_NOT_AUTHENTICATED");

export const AuthContext = createContext<IAuthContext>({} as any);
export const AuthContextEntry: FC<
    PropsWithChildren<IAuthContextEntryProps>
> = ({children, storage}) => {
    const [IsDeleted, SetIsDeleted] = useState(false);
    const {setAllowedIdleTime} = useContext(IdleContext);
    const client = useApolloClient();
    const currentToken = storage.getItem(LocalStorageKey.AUTH_TOKEN);

    if (!currentToken) {
        storage.removeItem(LocalStorageKey.USER_INFO);
    }

    const [Token, SetToken] = useState<string | null>(currentToken);
    Axios.defaults.headers.common["Authorization"] = `Bearer ${Token}`;

    const currentUserInfo = storage.getItem(LocalStorageKey.USER_INFO);
    let parsedUserInfo;
    if (currentUserInfo) {
        parsedUserInfo = JSON.parse(currentUserInfo);
    }
    const user = useUserQuery();

    useEffect(() => {
        if (Token) {
            Axios.defaults.headers.common["Authorization"] = `Bearer ${Token}`;
            storage.setItem(LocalStorageKey.AUTH_TOKEN, Token);
            user.refetch();
            RefreshToken();
        }
    }, [Token]);

    const SilentSetToken = useCallback(
        (token: string | null) => {
            if (token) {
                SetToken(token);
            }
        },
        [user, Token, SetToken],
    );

    const deleteAllCookies = useCallback(() => {
        const cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
    }, []);

    const Logout = useCallback(async (): Promise<void> => {
        await storage.clear();
        deleteAllCookies();
        SetToken(null);
        client.clearStore();
    }, [client, deleteAllCookies]);

    const RefreshToken = async (): Promise<any> => {
        return new Promise((resolve, reject) => {
            Axios.get<{ token: string }>("/token")
                .then(async ({data}) => {
                    if (data) {
                        storage.setItem(LocalStorageKey.AUTH_TOKEN, data.token);
                        storage.setItem(
                            LocalStorageKey.LAST_TOKEN_REFRESH,
                            new Date().toString(),
                        );
                    }
                    resolve(data);
                })
                .catch((response) => {
                    // 401: the token is no longer accepted (for example after a signing-key change). Without a
                    // logout the dashboard would wait for user info forever.
                    if (isRejectedSession(response?.response?.status)) {
                        Logout();
                    }
                    reject(response);
                });
        });
    };

    useEffect(() => {
        if (Token && isUnauthenticatedGraphQLError(user?.error)) {
            Logout();
        }
    }, [user?.error]);

    useEffect(() => {
        if (user && user?.data?.user?.isDeleted) {
            SetIsDeleted(true);
        } else {
            SetIsDeleted(false);
        }
    }, [user]);

    useEffect(() => {
        from(user);
    }, [user]);

    const from = useCallback((ui: UserQueryResult) => {
        const language = ui?.data?.user?.language ?? "HR";
        if (language && (language == "HR" || language == "EN")) {
            if (ui?.data?.user?.companySettings?.connectionName) {
                const overrides =
                    languageOverrides[ui?.data?.user?.companySettings?.connectionName];
                if (overrides) {
                    Object.keys(overrides).forEach((value, index, array) => {
                        const key = value as keyof typeof hr;
                        hr[key] = ((overrides as any)[key] as any)[language as any] as any;
                    });

                    i18n.reloadResources("hr").then();
                }
            }

            localStorage.setItem("language", language);
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{
                Token,
                RefreshToken,
                Logout,
                UserInfo: user?.data,
                SetToken,
                IsDeleted,
                FetchUserInfo: user.refetch,
                SilentSetToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
